import React, { useState } from 'react';
import { Collapse, List, ListItemButton, ListItemText } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import GenericMaster from './GenericMaster';
import FieldConfigMaster from './FieldConfigMaster';
import { MASTER_CONFIGS } from './configs';
import { PageFrame, PageHeader, Surface } from '@/components/ui';
import styles from './Masters.module.css';

interface SidebarGroup {
    label: string;
    keys: string[];
}

const SIDEBAR_GROUPS: SidebarGroup[] = [
    {
        label: 'Core Masters',
        keys: ['countries', 'categories', 'groups', 'distributors', 'client-classes'],
    },
    {
        label: 'Broker Masters',
        keys: ['brokers', 'scheme-wise-brokers'],
    },
    {
        label: 'Custody Masters',
        keys: ['custody-accounts'],
    },
    {
        label: 'Configuration',
        keys: ['field-configs', 'vendor-symbol-mappings'],
    },
];

const MasterManager: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
        Object.fromEntries(SIDEBAR_GROUPS.map((g) => [g.label, true]))
    );

    const currentConfig = MASTER_CONFIGS[activeTab];

    const toggleGroup = (label: string) => {
        setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    return (
        <PageFrame
            header={
                <PageHeader
                    title="General Masters"
                    subtitle="Reference data the rest of the system reads."
                />
            }
        >
            <div className={styles['masters-layout']}>
                {/* The picker is its own card, so the list on the right is one card
                    doing one job rather than a panel nested inside another. */}
                <Surface className={styles['masters-sidebar']}>
                    <List disablePadding>
                        {SIDEBAR_GROUPS.map((group) => (
                            <React.Fragment key={group.label}>
                                <ListItemButton
                                    onClick={() => toggleGroup(group.label)}
                                    sx={{ borderRadius: 'var(--radius-ctl)', py: 0.75 }}
                                >
                                    <ListItemText
                                        primary={group.label}
                                        primaryTypographyProps={{
                                            fontSize: 10.5,
                                            fontWeight: 700,
                                            color: 'var(--text-faint)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                        }}
                                    />
                                    {openGroups[group.label] ? (
                                        <ExpandLess sx={{ fontSize: 18, color: 'var(--text-faint)' }} />
                                    ) : (
                                        <ExpandMore sx={{ fontSize: 18, color: 'var(--text-faint)' }} />
                                    )}
                                </ListItemButton>
                                <Collapse in={openGroups[group.label]} timeout="auto" unmountOnExit>
                                    <List disablePadding>
                                        {group.keys.map((key) => {
                                            const index = MASTER_CONFIGS.findIndex((c) => c.key === key);
                                            const config = MASTER_CONFIGS[index];
                                            if (!config) return null;
                                            return (
                                                <ListItemButton
                                                    key={config.key}
                                                    selected={activeTab === index}
                                                    onClick={() => setActiveTab(index)}
                                                    sx={{
                                                        borderRadius: 'var(--radius-ctl)',
                                                        mb: 0.5,
                                                        pl: 3,
                                                        // Navy is the selected state everywhere else in the app;
                                                        // MUI's default blue would be a second accent.
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'var(--accent-soft)',
                                                            color: 'var(--accent)',
                                                            '&:hover': { backgroundColor: 'var(--accent-soft)' },
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={config.label}
                                                        primaryTypographyProps={{
                                                            fontSize: 13,
                                                            fontWeight: activeTab === index ? 600 : 400,
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        ))}
                    </List>
                </Surface>

                <div className={styles['masters-main']}>
                    {currentConfig.key === 'field-configs' ? (
                        <FieldConfigMaster config={currentConfig} />
                    ) : (
                        <GenericMaster config={currentConfig} />
                    )}
                </div>
            </div>
        </PageFrame>
    );
};

export default MasterManager;
